import toastr from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { generateURL } from 'utilities/hooks/urlGeneration';
import { SPARK_RESOURCE_TYPES } from 'constants/common';
import {
  SingleBoardNodesResponse,
  BoardNodesAttribute,
  BoardNodesResponse,
} from 'types/BoardNode';

type MutationVariables = Partial<BoardNodesAttribute> & { id?: string };

export function useAddBoardNode(boardID: string | null | undefined) {
  const queryClient = useQueryClient();
  const addBoardNodes = (variables: MutationVariables) => {
    if (!boardID) {
      return Promise.reject('board ID can not be null');
    }
    const url = generateURL(SPARK_RESOURCE_TYPES.BOARD_NODES);
    const prevData = queryClient.getQueryData<BoardNodesResponse>([
      SPARK_RESOURCE_TYPES.BOARD_NODES,
      boardID,
    ]);
    const count = prevData?.meta?.total || 0;
    const { id, ...rest } = variables;

    const body = {
      data: {
        ...(id && { id: id }),
        type: SPARK_RESOURCE_TYPES.BOARD_NODES,
        attributes: { boardID, ...rest, sortOrder: count + 1 },
      },
    };
    return fetchWrapper<SingleBoardNodesResponse>({
      url: url,
      method: 'POST',
      body,
    });
  };

  return useMutation(['addBoardNodes', boardID], addBoardNodes, {
    onMutate: async (variables) => {
      const { id, ...rest } = variables;
      const temNode = {
        id: id,
        type: 'boardNodes',
        attributes: rest,
      };

      queryClient.setQueryData<BoardNodesResponse>(
        [SPARK_RESOURCE_TYPES.BOARD_NODES, boardID],
        (old) => {
          const newNodes = old?.data;
          //@ts-ignore
          newNodes?.push(temNode);
          return {
            ...old,
            data: newNodes || [],
          };
        }
      );
    },
    onError: (error) => {
      if (error instanceof Error) {
        toastr.error(`Failed to Add Board Nodes: ${error.message}`);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([
        SPARK_RESOURCE_TYPES.BOARD_NODES,
        boardID,
      ]);
    },
  });
}
